/**
 * The import pipeline.
 *
 * Seven stages, each its own module, none of which touches the network or the
 * database. Publishing is a separate, explicit call the UI makes after the
 * operator has filled in category and tags — a parser that could reach the
 * database would make every future format a data-integrity risk.
 *
 * The importer, not the parser, derives variables, metadata, preview, and the
 * content hash. Those are computed identically for every format; six parsers
 * each owning a copy would be six copies slowly drifting apart.
 *
 * Nothing here throws for bad input. A malformed document comes back as a
 * complete ParseResult carrying its errors, which is what lets the import
 * screen show a preview of a half-broken template beside the list of what is
 * wrong with it.
 */

import { IMPORT_VERSION, PARSE_STAGES, SOURCES } from './constants';
import { emptyAssets, emptyPreview, emptyReport } from './types';
import { normalizeInput } from './normalizeInput';
import { normalizeDocument } from './normalizeDocument';
import { parserRegistry } from './parserRegistry';
import { validateEditorDocument } from './documentValidator';
import { extractVariables } from './extractVariables';
import { buildPreview } from './buildPreview';
import { contentHash } from './contentHash';
import { deriveLibraryId } from './libraryId';
import { registerBuiltInParsers } from './parsers';

registerBuiltInParsers();

const EMPTY_DOCUMENT = { canvas_width: 0, canvas_height: 0, pages: [] };

/** A fully-shaped result carrying one fatal error. Every key present, always. */
const failed = (message, metadata) => ({
  document: EMPTY_DOCUMENT,
  assets: emptyAssets(),
  variables: [],
  metadata: {
    parser: null,
    parser_version: null,
    source: SOURCES.IMPORTED,
    import_version: IMPORT_VERSION,
    library_id: null,
    library_id_explicit: false,
    content_hash: null,
    imported_at: new Date().toISOString(),
    ...metadata,
  },
  preview: emptyPreview(),
  report: { ...emptyReport(), errors: [message] },
});

/**
 * Run a raw input through the pipeline.
 *
 * @param {File|Blob|string} raw
 * @param {{signal?: AbortSignal, onProgress?: Function, metadata?: Object}} [options]
 * @returns {Promise<import('./types').ParseResult>}
 */
const runImport = async (raw, options = {}) => {
  const { signal, onProgress = () => {}, metadata = {} } = options;
  const aborted = () => Boolean(signal && signal.aborted);

  if (aborted()) return failed('Import aborted', metadata);

  // ── 1. Normalize input ────────────────────────────────────────────────────
  onProgress(PARSE_STAGES.NORMALIZE_INPUT, 0);
  let input;
  try {
    input = await normalizeInput(raw);
  } catch (err) {
    return failed(err.message, metadata);
  }
  if (aborted()) return failed('Import aborted', metadata);

  // ── 2. Select parser ──────────────────────────────────────────────────────
  onProgress(PARSE_STAGES.SELECT_PARSER, 0.1);
  const parser = parserRegistry.getParser(input);
  if (!parser) {
    return failed(
      input.filename
        ? `No parser can read "${input.filename}"`
        : 'No parser can read this content',
      metadata,
    );
  }

  // ── 3. Parse ──────────────────────────────────────────────────────────────
  onProgress(PARSE_STAGES.PARSE, 0.2);
  let parsed;
  try {
    parsed = await parser.parse(input, { signal, onProgress, metadata });
  } catch (err) {
    return failed(`${parser.label} failed: ${err.message}`, { ...metadata, parser: parser.id });
  }
  if (aborted()) return failed('Import aborted', metadata);

  const report = { ...emptyReport(), ...(parsed.report || {}) };
  const assets = { ...emptyAssets(), ...(parsed.assets || {}) };

  // Stages 4-6 are wrapped together. Individually they are pure functions over
  // a document that has already parsed, so nothing here is expected to throw —
  // but "expected" is not a guarantee, and this module's contract is that it
  // always returns a result. A stack overflow in canonicalize on a pathological
  // document must surface as an error the operator can read, not as an
  // unhandled rejection that leaves the screen spinning forever.
  try {
    // ── 4. Normalize document ───────────────────────────────────────────────
    onProgress(PARSE_STAGES.NORMALIZE_DOCUMENT, 0.5);
    const { document, warnings: normalizeWarnings } = normalizeDocument(parsed.document);
    report.warnings = [...report.warnings, ...normalizeWarnings];

    // ── 5. Validate ─────────────────────────────────────────────────────────
    onProgress(PARSE_STAGES.VALIDATE, 0.6);
    const { errors } = validateEditorDocument(document);
    report.errors = [...report.errors, ...errors];

    // ── 6. Derive ───────────────────────────────────────────────────────────
    onProgress(PARSE_STAGES.DERIVE, 0.75);
    const { variables, warnings } = extractVariables(document);
    report.warnings = [...report.warnings, ...warnings];

    const hash = await contentHash(document);
    if (hash === null) {
      report.warnings.push(
        'Content hashing is unavailable in this browser context — duplicate detection is disabled for this import',
      );
    }

    // Trimmed to match deriveLibraryId, which ignores a whitespace-only id.
    // Without the trim, "   " would be reported as explicit while the id
    // actually used was derived from the name — and the screen would warn
    // about replacing something the operator never named.
    const explicitLibraryId = Boolean(
      parsed.document
      && typeof parsed.document.library_id === 'string'
      && parsed.document.library_id.trim(),
    );

    // ── 7. Assemble ─────────────────────────────────────────────────────────
    onProgress(PARSE_STAGES.ASSEMBLE, 1);
    return {
      document,
      assets,
      variables,
      metadata: {
        parser: parser.id,
        parser_version: IMPORT_VERSION,
        source: SOURCES.IMPORTED,
        import_version: IMPORT_VERSION,
        library_id: deriveLibraryId(document),
        // Whether the id came from the document or was derived from its name.
        // An explicit id targets an existing record for replacement, which the
        // import screen has to be able to warn about.
        library_id_explicit: explicitLibraryId,
        content_hash: hash,
        imported_at: new Date().toISOString(),
        ...metadata,
      },
      preview: buildPreview(document),
      report,
    };
  } catch (err) {
    return failed(`Could not process this template: ${err.message}`, {
      ...metadata,
      parser: parser.id,
    });
  }
};

export const TemplateImporter = { import: runImport };
